import { DataAggregationFunction, DataFieldUsage, DataSchemaItemDto, DataSchemaItemGroupKind, SchemaFieldTargetDto } from 'src/app/services/api.services';
import { INamedElement, NamedElementHierarchyCollection } from './Collections';
import { DataSchemaItemGroup } from './DataSchemaItemGroup';
import { ItemDisplayOptions } from './ItemDisplayOptions';
import { DataField } from './DataField';
import { VisualField } from '../visuals/VisualField';
import { DataAggregation } from './DataAggregation';
import { SchemaItemFieldTarget } from '../visuals/VisualFieldTarget';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { isNullOrUndefined } from '../../shared/utils';

export class DataSchemaItem implements INamedElement {
  readonly children = new DataSchemaItem.ChildCollection(this);

  constructor(
    public readonly itemGroup: DataSchemaItemGroup,
    public readonly name: string,
    public readonly parent?: DataSchemaItem) { }

  get model() { return this.source.model; }
  get source() { return this.schema.source; }
  get schema() { return this.itemGroup.schema; }

  readonly path = [...(this.parent?.path ?? []), this.name];

  field: DataField;

  itemDisplay = new ItemDisplayOptions({});

  setFromDto(dto: DataSchemaItemDto) {
    this.field = this.model.fields.get(dto.field);

    this.itemDisplay = new ItemDisplayOptions(dto.itemDisplay);

    dto.children?.forEach(x => this.children.add(x.name).setFromDto(x));
  }

  /**
   * Returns hierarchy starting at it top ancestor down to this item itself.
   */
  *getItemHierarchy(): Iterable<DataSchemaItem> {
    let hierarchy = [];

    for (let i: DataSchemaItem = this; !!i; i = i.parent)
      hierarchy.push(i);

    for (let i = hierarchy.length - 1; i >= 0; --i)
      yield hierarchy[i];
  }


  getDefaultUsage(): DataFieldUsage {
    if (!isNullOrUndefined(this.field.defaultUsage))
      return this.field.effectiveDefaultUsage;

    if (this.itemGroup.kind === DataSchemaItemGroupKind.Measures)
      return DataFieldUsage.Measure;

    return this.field.effectiveDefaultUsage;
  }

  getDefaultAggregation(): DataAggregation | null {
    let measureGroup = this.getDefaultUsage() == DataFieldUsage.Measure
                    || this.field.effectiveMustHaveAggregation;

    return this.field.isValueAggregated
        ? null
        : (this.field.effectiveDefaultAggregation
          ?? (measureGroup ? this.field.dataType.defaultAggregation : null));
  }

  getEditorIcon() {
    if (this.getDefaultUsage() == DataFieldUsage.Measure) {
      return 'fas fa-ruler-combined';
    }

    return this.field.getEditorIcon();
  }

  /**
   * Constructs long title of this schema item, e.g.
   * 'Group - Item'
   * 'Group - Parent Item - Item'
   */
  formatLongTitle(separator = ' - ') {
    let parts = [];

    parts.push(this.itemGroup.name);

    for (let i of this.getItemHierarchy())
      parts.push(i.name);

    return parts.join(separator);
  }

  getIdentifierName(): string {
    return this.formatLongTitle('.');
  }

  getIdentifierExpr(): string {
    return `[${this.getIdentifierName()}]`;
  }

  createSchemaFieldTargetDto(): SchemaFieldTargetDto {
    if (!!this.parent?.parent) {
      throw new Error(`createSchemaFieldTargetDto() supports only one ancestor level`);
    }

    if (this.parent) {
      return {
        schema: this.schema.getSchemaTargetDto(),
        group: this.itemGroup.name,
        field: this.parent.name,
        childField: this.name,
        aggregation: this.getDefaultAggregation()?.value,
      };
    }
    else {
      return {
        schema: this.schema.getSchemaTargetDto(),
        group: this.itemGroup.name,
        field: this.name,
        childField: null,
        aggregation: this.getDefaultAggregation()?.value,
      };
    }
  }


  private visualFieldTarget: SchemaItemFieldTarget;

  createVisualFieldTarget(aggregation?: DataAggregation) {
    if (!aggregation) {
      return this.visualFieldTarget ??= new SchemaItemFieldTarget(this, null);
    }

    return new SchemaItemFieldTarget(this, aggregation);
  }


  toDto(): DataSchemaItemDto {
    return {
      name: this.name,
      children: [...this.children.map(x => x.toDto())],
      field: deepClone(this.field?.reference),
      itemDisplay: deepClone(this.itemDisplay.dto),
    };
  }
}

export namespace DataSchemaItem {
  export class ChildCollection extends NamedElementHierarchyCollection<DataSchemaItem> {
    constructor(public readonly i: DataSchemaItem) {
      super();
    }

    add(name: string) {
      let item = new DataSchemaItem(this.i.itemGroup, name, this.i);
      this._add(item);
      return item;
    }

    getOrAdd(name: string) {
      return this.find(name) ?? this.add(name);
    }

    protected findChild(item: DataSchemaItem, name: string) {
      return item.children.find(name);
    }

    protected getBase() {
      return this.i.model.base?.schemaItems.find(
        this.i.source.reference,
        this.i.schema.reference,
        this.i.itemGroup.name,
        ...this.i.path)?.children;
    }

    protected getExtensions() {
      return this.i.model.base?.extensions.map(
        x => x.schemaItems.find(
          this.i.source.reference,
          this.i.schema.reference,
          this.i.itemGroup.name,
          ...this.i.path)?.children) ?? [];
    }
  }
}
